import {
	AssessmentVulnerabilityData,
	IssueStatusEnum,
	LegendLabel,
	LikelihoodLabel,
	RiskMatrixCellData,
	SeverityLabel
} from "../../Assessments";
import { BulkImportError } from "../../BulkImport";
import {
	DiscountType,
	DocumentType,
	PaymentSummary,
	QuoteTotal
} from "../../Documents";
import {
	BundleProduct,
	Distributor,
	Product,
	ProductBillingType,
	ProductPricingPlan,
	ProductSource,
	ProductTaxType,
	ProductType,
	SelectedCustomDistributorDetails
} from "../../Products";
import {
	HealthStatusVulnerabilitiesData,
	QbrHealthStatusEnum
} from "../../QBR";
import { AutoPriceUpdateDetails, CustomField } from "../../Settings";
import { SignatureBlockLabels } from "../../Users";

import isNull from "lodash/isNull";

import { isValidTextString } from "../../Utils";
import { SKUPricingPlanFormValues, SKUProduct } from "../../Vendor/SKU";
import { DocumentPageDetails } from "./../../Documents/Revision";
import { CurrencySettings } from "../../common";

export enum BlockType {
	IMAGE = "image",
	VIDEO = "video",
	PAGE_BREAK = "page_break",
	QUOTE = "pricing_table",
	SIGNATURE = "signature",
	GRID = "table",
	TEXT = "text",
	PDF = "pdf",
	RISK_MATRIX = "risk_matrix",
	RISK_BAR = "risk_bar",
	RISK_LISTING = "risk_listing",
	HEALTH_STATUS = "health_status",
	HEALTH_LEGEND = "health_legend",
	PAYMENT_SUMMARY = "payment_summary"
}
export const BlockTypeToNameMapping = {
	[BlockType.IMAGE]: "Image",
	[BlockType.VIDEO]: "Video",
	[BlockType.PAGE_BREAK]: "Page Break",
	[BlockType.QUOTE]: "Quote",
	[BlockType.SIGNATURE]: "Signature",
	[BlockType.GRID]: "Grid",
	[BlockType.TEXT]: "Text",
	[BlockType.PDF]: "PDF",
	[BlockType.RISK_MATRIX]: "Risk Matrix",
	[BlockType.RISK_BAR]: "Risk Bar",
	[BlockType.RISK_LISTING]: "Risk Listing",
	[BlockType.HEALTH_LEGEND]: "Health Legend",
	[BlockType.HEALTH_STATUS]: "Health Status",
	[BlockType.PAYMENT_SUMMARY]: "Payment Summary"
};

//checks wheter any grid block component contains any empty block component
export const validateGridBlockEmpty = (
	block: Block,
	blockMapper: BlockMapper
): boolean => {
	if (block.children_blocks.length === 0) {
		return true;
	} else {
		const childrenIds = block.children_blocks;
		const blockDatas = childrenIds.map(blockId => {
			if (blockId) {
				return blockMapper[blockId];
			}
			return null;
		});
		const allCellsEmptyState: (boolean | null)[] = blockDatas.map(
			blockData => {
				if (blockData?.type === BlockType.IMAGE) {
					return validateImageBlockEmpty(
						blockData.data as ImageBlockData
					);
				} else if (blockData?.type === BlockType.VIDEO) {
					return validateVideoBlockEmpty(
						blockData.data as VideoBlockData
					);
				} else if (blockData?.type === BlockType.TEXT) {
					return validateTextBlockEmpty(
						blockData.data as TextBlockData
					);
				} else if (blockData?.type === BlockType.SIGNATURE) {
					//everytime return empty as null for signature block
					return false;
				} else {
					return null;
				}
			}
		);

		return !allCellsEmptyState.includes(false);
	}
};

export const validateImageBlockEmpty = (blockData: ImageBlockData): boolean => {
	return !blockData.image_url && !isNull(blockData.image_url);
};
export const validateVideoBlockEmpty = (blockData: VideoBlockData): boolean => {
	if (blockData?.is_youtube) {
		return !blockData?.youtube_id;
	}
	return !blockData.video_url && !isNull(blockData.video_url);
};
export const validateTextBlockEmpty = (blockData: TextBlockData): boolean => {
	const htmlEl = document.createElement("html");
	htmlEl.innerHTML = blockData.text;
	const bodyElText = htmlEl.getElementsByTagName("body")[0];
	return !isValidTextString(bodyElText.textContent ?? "");
};

export enum QuoteBlockTheme {
	DARK_BLUE = "dark_blue",
	MID_BLUE = "mid_blue",
	BRIGHT_BLUE = "bright_blue",
	GREY = "grey",
	CUSTOM = "custom",
	GREEN = "green",
	SKYBLUE = "sky_blue",
	LAVENDER = "lavender",
	YELLOW = "yellow",
	WHITE = "white",
	PINK = "pink"
}

export enum QuoteBlockStyleDefaults {
	DEFAULT_FONT = "Default Font",
	DEFAULT_FONT_COLOR_WHITE = "rgba(255, 255, 255, 1)",
	DEFAULT_FONT_COLOR_BLACK = "rgba(0, 0, 0, 1)",
	DEFAULT_BACKGROUND_COLOR = "rgba(47, 112, 255, 1)",
	DEFAULT_BACKGROUND_COLOR_SKY_BLUE = "rgba(207, 231, 253, 1)"
}

export class BlockPosition {
	top = 0;
	left = 0;
}

export class BlockSize {
	height = 0;
	width = 0;
}

export interface Block {
	children_blocks: (string | null)[];
	created: Date;
	id: string;
	data: BlockData | null;
	type: BlockType | null;
	updated: Date;
	parent_block_id?: string;
	position?: BlockPosition;
	size?: BlockSize;
	hide: boolean;
	draft?: boolean | null;
}

export interface BlockResponse extends Block {
	payment_summary: PaymentSummary | null;
}

export interface BulkImportPricingTableResponse {
	updatedBlockOpt?: BlockResponse | null;
	errors: BulkImportError[];
	rowsSkippedCount: number;
}

export class GOFBundledProductChanges {
	quantity: number;
	constructor(quantity: number) {
		this.quantity = quantity;
	}
}
export class GOFProductChanges {
	quantity: number;
	bundled_products: GOFBundledProductChanges[];
	constructor(
		quantity: number,
		bundled_products: GOFBundledProductChanges[]
	) {
		this.quantity = quantity;
		this.bundled_products = bundled_products;
	}
}
export class GOFPaymentSummaryRequestData {
	blocks_metadata_gof: GOFProductChanges[] = [];
}
export class GOFBlockSection {
	selected_products: string[];
	products: GOFProductChanges[];
	constructor(selected_products: string[], products: GOFProductChanges[]) {
		this.selected_products = selected_products;
		this.products = products;
	}
}
export class GOFBlockDataChanges {
	sections: GOFBlockSection[];
	tax: number;
	total: QuoteTotal;
	constructor(sections: GOFBlockSection[], tax: number, total: QuoteTotal) {
		this.sections = sections;
		this.tax = tax;
		this.total = total;
	}
}
export class GOFBlockChanges {
	id: string;
	data: GOFBlockDataChanges;
	updated: Date;
	constructor(id: string, data: GOFBlockDataChanges, updated: Date) {
		this.id = id;
		this.data = data;
		this.updated = updated;
	}
}

export interface PDFBlock {
	readonly blockType: BlockType;
	readonly parentId: string;
	readonly pageId: string;
	readonly position?: BlockPosition;
}

export type BlockMapper = Record<string, Block>;

class BlocksCountMap {
	product_count = 0;
}

export class BlockListResponse {
	data: Block[] = new Array<Block>();
	filtered_count_map = new BlocksCountMap();
}

export interface BlockListRequest {
	documentId: string;
	revisionId: string;
}

export class BlockRequest implements Omit<Block, "id" | "created" | "updated"> {
	document_id?: string;
	template_id?: string;
	children_blocks: (string | null)[] = [];
	data: BlockData | null = null;
	type: BlockType | null = null;
	position?: BlockPosition;
	size?: BlockSize;
	hide = false;
}

export type BlockData =
	| RiskListingBlockData
	| HealthStatusBlockData
	| HealthLegendBlockData
	| TextBlockData
	| ImageBlockData
	| VideoBlockData
	| QuoteBlockData
	| GridBlockData
	| PageBreakBlockData
	| SignatureBlockData
	| RiskMatrixBlockData
	| PDFBlockData;

export enum Alignment {
	CENTER = "center",
	LEFT = "left",
	RIGHT = "right"
}

export enum VerticalAlignment {
	CENTER = "center",
	TOP = "top",
	BOTTOM = "bottom"
}

export class TextBlockData {
	text = "";
	background_color: string | null = null;
}

export class PDFBlockData {
	pdf_url = "";
}

export class HealthLegendBlockData {
	health_values: QbrHealthStatusEnum[] = [];
}

export enum ImageBlockDataDisplayType {
	AUTO = "auto",
	CONTAIN = "contain",
	COVER = "cover",
	ORIGINAL = "original"
}

export class ImageBlockData {
	caption = "";
	image_url = "";
	display: ImageBlockDataDisplayType = ImageBlockDataDisplayType.CONTAIN;
	align: Alignment = Alignment.CENTER;
	vertical_align: VerticalAlignment = VerticalAlignment.CENTER;
	width = 0;
	height = 80;
	is_full_width = false;
}

export class VideoBlockData {
	caption = "";
	video_url = "";
	thumbnail = "";
	align: Alignment = Alignment.CENTER;
	width = 0;
	height = 300;
	autoplay = false;
	youtube_id = "";
	is_youtube = false;
}

// Pricing Table

export type QuoteLabelEnums = keyof QuoteLabels;

export interface UOMLabels {
	each_label: string;
	case_label: string;
	per_hour_label: string;
	per_month_label: string;
}

export interface QuoteLabels {
	monthly_label: string;
	quarterly_label: string;
	semi_annually_label: string;
	upfront_label: string;
	annually_label: string;
	weekly_label: string;
	additional_setup_price_label: string;
	amount_label: string;
	description_label: string;
	discount_label: string;
	price_label: string;
	quantity_label: string;
	sales_tax_label: string;
	tax_label: string;
	discount_help_label: string;
	weekly_recurring_label?: string;
	monthly_recurring_label?: string;
	quarterly_recurring_label?: string;
	semi_annually_recurring_label?: string;
	annually_recurring_label?: string;
	item_margin_label?: string;
	total_label?: string;
	total_margin_label?: string;
	commission_label?: string;
	total_commission_label?: string;
	additional_setup_price_discount_label: string;
	uom_label: UOMLabels;
	expiration_date_label: string;
	link_to_qty_label: string;
	linked_to_qty_label: string;
}

export const DefaultQuoteLabels: QuoteLabels = {
	additional_setup_price_label: "Additional Setup Fee",
	amount_label: "Amount",
	annually_label: "Annually",
	description_label: "Description",
	discount_label: "Discount",
	monthly_label: "Monthly",
	price_label: "Price",
	quantity_label: "Quantity",
	quarterly_label: "Quarterly",
	sales_tax_label: " Sales Tax",
	semi_annually_label: "Semi-Annually",
	tax_label: "Tax",
	upfront_label: "One-time",
	weekly_label: "Weekly",
	discount_help_label: "On Subtotal",
	item_margin_label: "Item Margin",
	total_label: "Total",
	total_margin_label: "Total Margin",
	commission_label: "Commission",
	total_commission_label: "Total Commission",
	additional_setup_price_discount_label: "Discount",
	uom_label: {
		each_label: "Each",
		case_label: "Case",
		per_hour_label: "Per Hour",
		per_month_label: "Per Month"
	},
	expiration_date_label: "Expiration Date",
	link_to_qty_label: "Link to Qty.",
	linked_to_qty_label: "Linked to Qty."
};

export class QuoteMargin {
	sellPrice = 0;
	costPrice = 0;
	margin = 0;
	discount = 0;

	get flat() {
		return this.margin - this.discount;
	}

	get percentage() {
		return (
			(this.sellPrice &&
				Math.round((this.flat / this.sellPrice) * 100 * 100) / 100) ||
			0
		);
	}
}

export interface BundledQuoteProduct
	extends Omit<
		QuoteProduct,
		| "block_id"
		| "hide_additional_price"
		| "logo_square"
		| "bundled_products"
		| "should_add_setup_price_to_margin"
		| "show_child_quantity"
	> {
	is_optional: boolean;
	readonly is_selected: boolean;
	quantity: number;
	readonly hide_additional_price?: boolean;
	readonly sku_id?: string;
}

export interface QuoteProduct {
	additional_setup_price_label?: string;
	bundled_products?: BundledQuoteProduct[];
	bundled_skus?: Partial<BundledQuoteProduct>[];
	show_individual_pricing?: boolean;
	pricings?: ProductPricingPlan[];
	commission?: number;
	custom_field_form_values?: Record<string, string>;
	custom_fields?: CustomField[];
	description: string;
	discount_percentage: number;
	discount_type: DiscountType;
	discount: number;
	hide_additional_price: boolean;
	is_setup_fee_linked_to_quantity: boolean;
	hide_child_items_in_quote_block: boolean;
	id?: string;
	item_images: string[];
	logo_square: string | null;
	manufacturer_part_no: string | null;
	manufacturer: string | null;
	name: string;
	pricing?: ProductPricingPlan[];
	product_category: string;
	product_thumbnail_size: ProductThumbnailSize | null;
	product_source?: ProductSource;
	product_type: ProductType | null;
	quantity: number;
	selected_billing_period: ProductBillingType;
	selected_cost_price: number;
	selected_sell_price?: number;
	selected_distributor?: string | undefined;
	selected_images: string[];
	selected_pricing_id?: string | null;
	selected_supplier_id?: string | null;
	selected_setup_discount: number;
	selected_setup_price: number;
	selected_warehouse?: string | null;
	sell_price: number;
	setup_discount_percentage?: number;
	setup_discount_type: DiscountType;
	should_add_setup_price_to_margin: boolean;
	show_individual_product_pricing: boolean;
	show_child_quantity: boolean | null;
	sub_total: number;
	tax_category_id?: string;
	tax_type?: ProductTaxType;
	unit_of_measure?: string;
	unit_of_measure_abbreviation?: string;
	vendor_id: string;
	vendor_product_id: string;
	effective_setup_price?: number;
	effective_sell_price?: number;
	item_margin_percent?: number;
	item_margin_value?: number;
	mandatory_custom_field_check_require: boolean;
	vendor_name?: string;
	vendor_sku_number?: string;
	auto_price_update_details: AutoPriceUpdateDetails | null;
	selected_custom_distributor_details?: SelectedCustomDistributorDetails;
	asin?: string;
}

export enum QuoteSectionTypes {
	MANDATORY = "mandatory",
	MULTIPLE = "multiple",
	SINGLE = "single"
}

export enum ProductThumbnailSize {
	SMALL = "small",
	MEDIUM = "medium",
	LARGE = "large"
}
export interface EditQuantityRange {
	min_quantity: number | null;
	max_quantity: number | null;
}

export class QuoteSectionData {
	name = "";
	subtitle = "";
	hide_images = false;
	hide_description = false;
	products: QuoteProduct[] = new Array<QuoteProduct>();
	type: QuoteSectionTypes = QuoteSectionTypes.MANDATORY;
	selected_products: string[] = new Array<string>();
	hide_unit_price = false;
	hide_quantity = false;
	hide_discount = false;
	hide_amount = false;
	allow_client_editing = false;
	min_quantity: number | null = null;
	max_quantity: number | null = null;
	section_thumbnail_size: ProductThumbnailSize | null = null;
	total: QuoteTotal = new QuoteTotal();
	total_margin_value = 0;
	total_margin_value_percentage = 0;
	total_commission = 0;
	tax_percentage = 0;
	tax = 0;
	constructor({
		hideDiscount,
		hideImages,
		hideDescription
	}: {
		hideDiscount?: boolean;
		hideImages?: boolean;
		hideDescription?: boolean;
	} = {}) {
		this.hide_discount = hideDiscount ?? false;
		this.hide_images = hideImages ?? false;
		this.hide_description = hideDescription ?? false;
	}
}

export enum EditQuantityOption {
	EDITING_NOT_ALLOWED,
	NO_LIMIT,
	MAX_LIMIT,
	MIN_LIMIT,
	BOTH_LIMITS_SET
}

export enum EditQuantityInputOption {
	Min = "min",
	Max = "max"
}

export class PricingTableStyles {
	font: string = QuoteBlockStyleDefaults.DEFAULT_FONT;
	pricing_table_theme: {
		font_color: string;
		background_color: string;
		theme_choice: string;
	} = {
		font_color: QuoteBlockStyleDefaults.DEFAULT_FONT_COLOR_WHITE,
		background_color: QuoteBlockStyleDefaults.DEFAULT_BACKGROUND_COLOR,
		theme_choice: QuoteBlockTheme.BRIGHT_BLUE
	};
}

export interface TaxLevelToAmountMap {
	tax_level_name: string;
	tax_amount: number;
}
export interface TaxCatgoryToAmountMap {
	tax_amount: number;
	tax_category_name: string;
	levels: Array<TaxLevelToAmountMap>;
}

export class QuoteBlockData {
	tax = 0;
	discount = 0;
	name = "";
	sections: QuoteSectionData[] = [];
	total: QuoteTotal = new QuoteTotal();
	label?: QuoteLabels;
	pricing_table_styles: PricingTableStyles = new PricingTableStyles();
	tax_percentage = 0;
	additional_setup_price_consistent = false;
	hide_tax = false;
	hide_images = false;
	hide_description = false;
	total_margin_value = 0;
	total_margin_percentage = 0;
	tax_category_to_amount_view_seq = new Array<TaxCatgoryToAmountMap>();
	total_commission = 0;

	constructor(
		docType?: DocumentType,
		isNewQuoteBlockDesign?: boolean,
		hideDiscount?: boolean,
		name?: string,
		isQuoteBlockCompact?: boolean
	) {
		this.name = name || "";
		this.sections = new Array<QuoteSectionData>(
			new QuoteSectionData({
				hideDiscount,
				hideDescription: !!isQuoteBlockCompact,
				hideImages: !!isQuoteBlockCompact
			})
		);
		if (
			docType &&
			[
				DocumentType.SPECIFIC_ORDER_FORM,
				DocumentType.GENERIC_ORDER_FORM
			].includes(docType)
		) {
			const newSection = new QuoteSectionData({
				hideDiscount,
				hideDescription: !!isQuoteBlockCompact,
				hideImages: !!isQuoteBlockCompact
			});
			newSection.type = QuoteSectionTypes.MULTIPLE;
			newSection.allow_client_editing = true;
			this.sections = [newSection];
		}
		if (isNewQuoteBlockDesign) {
			const newPricingTableStyles = new PricingTableStyles();
			newPricingTableStyles.font = QuoteBlockStyleDefaults.DEFAULT_FONT;
			newPricingTableStyles.pricing_table_theme = {
				font_color: QuoteBlockStyleDefaults.DEFAULT_FONT_COLOR_BLACK,
				background_color: `linear-gradient(to bottom, ${QuoteBlockStyleDefaults.DEFAULT_BACKGROUND_COLOR_SKY_BLUE}, #FFF 95px)`,
				theme_choice: QuoteBlockTheme.SKYBLUE
			};
			this.pricing_table_styles = newPricingTableStyles;
		}
	}

	public static getSelectedProducts = (
		blockData: QuoteBlockData
	): QuoteProduct[] => {
		const selectedProducts: QuoteProduct[] = (blockData?.sections || [])
			.map(section =>
				section.products.filter(
					product =>
						product.id &&
						(section.selected_products || []).includes(product.id)
				)
			)
			?.reduce((acc, val) => acc.concat(val), []);

		return selectedProducts;
	};
}

export class RiskMatrixBlockData {
	cell_data: RiskMatrixCellData[] = new Array<RiskMatrixCellData>();
	likelihood_header_label = "";
	severity_header_label = "";
	severity_labels: SeverityLabel[] = [];
	likelihood_labels: LikelihoodLabel[] = [];
	legend_labels: LegendLabel[] = [];
}

export class RiskListingBlockData {
	category_id = "";
	filter_on_status: IssueStatusEnum | null = null;
	vulnerability_data_list: AssessmentVulnerabilityData[] =
		new Array<AssessmentVulnerabilityData>();
}
export class RiskBarBlockData {
	score = 0;
	is_default_score = true;
}

export class HealthStatusBlockData {
	category_id = "";
	filter_on_status: IssueStatusEnum | null = null;
	health_status_block_data: HealthStatusVulnerabilitiesData[] =
		new Array<HealthStatusVulnerabilitiesData>();
}

export class GridBlockData {
	rows = 1;
	columns = 2;
}

export class SignatureBlockData {
	align: Alignment = Alignment.LEFT;
	width = "350px";
	signer_index = 0;
	is_signed = false;
	signature_block_labels?: SignatureBlockLabels = new SignatureBlockLabels();
	signer?: string | null = null;
}

export class PageBreakBlockData {}

export const createNewBlock = (
	blockType: BlockType,
	docType?: DocumentType,
	isNewQuoteBlockDesign?: boolean,
	hideDiscount?: boolean,
	name?: string,
	isQuoteBlockCompact?: boolean
): BlockRequest => {
	const blockDataRequest = new BlockRequest();
	blockDataRequest.type = blockType;
	switch (blockDataRequest.type) {
		case BlockType.IMAGE: {
			blockDataRequest.data = new ImageBlockData();
			break;
		}
		case BlockType.VIDEO: {
			blockDataRequest.data = new VideoBlockData();
			break;
		}
		case BlockType.PAGE_BREAK: {
			blockDataRequest.data = null;
			break;
		}
		case BlockType.QUOTE: {
			blockDataRequest.data = new QuoteBlockData(
				docType,
				isNewQuoteBlockDesign,
				hideDiscount,
				name,
				isQuoteBlockCompact
			);
			break;
		}
		case BlockType.SIGNATURE: {
			blockDataRequest.data = new SignatureBlockData();
			break;
		}
		case BlockType.GRID: {
			blockDataRequest.data = new GridBlockData();
			blockDataRequest.children_blocks = [null, null];
			break;
		}
		case BlockType.TEXT: {
			blockDataRequest.data = new TextBlockData();
			break;
		}
		default: {
			break;
		}
	}
	return blockDataRequest;
};

export const BLOCK_PANEL_SOURCE = "BLOCK_PANEL_SOURCE";
export const DOCUMENT_PAPER = "DOCUMENT_PAPER";
export const IMAGE_BLOCK_DRAGGABLE = "IMAGE_BLOCK_DRAGGABLE";
export const VIDEO_BLOCK_DRAGGABLE = "VIDEO_BLOCK_DRAGGABLE";
export const TEXT_BLOCK_DRAGGABLE = "TEXT_BLOCK_DRAGGABLE";
export const QUOTE_BLOCK_DRAGGABLE = "QUOTE_BLOCK_DRAGGABLE";
export const SIGNATURE_BLOCK_DRAGGABLE = "SIGNATURE_BLOCK_DRAGGABLE";
export const GRID_BLOCK_DRAGGABLE = "GRID_BLOCK_DRAGGABLE";
export const PAGE_BREAK_BLOCK_DRAGGABLE = "PAGE_BREAK_BLOCK_DRAGGABLE";
export const PAYMENT_SUMMARY_BLOCK_DRAGGABLE =
	"PAYMENT_SUMMARY_BLOCK_DRAGGABLE";
export const PDF_PAGE = "PDF_PAGE";

// TODO: Move all above enums inside DraggableType
export enum DraggableType {
	NewBlock = "NEW_BLOCK",
	SavedBlock = "SAVED_BLOCK",
	Block = "BLOCK"
}

export const QUOTE_SECTION = "QUOTE_SECTION";
export const QUOTE_SECTION_PARENT = "QUOTE_SECTION_PARENT";

export const draggableIdToBlockTypeMapper: Record<string, BlockType> = {
	[IMAGE_BLOCK_DRAGGABLE]: BlockType.IMAGE,
	[VIDEO_BLOCK_DRAGGABLE]: BlockType.VIDEO,
	[TEXT_BLOCK_DRAGGABLE]: BlockType.TEXT,
	[QUOTE_BLOCK_DRAGGABLE]: BlockType.QUOTE,
	[SIGNATURE_BLOCK_DRAGGABLE]: BlockType.SIGNATURE,
	[GRID_BLOCK_DRAGGABLE]: BlockType.GRID,
	[PAGE_BREAK_BLOCK_DRAGGABLE]: BlockType.PAGE_BREAK,
	[PAYMENT_SUMMARY_BLOCK_DRAGGABLE]: BlockType.PAYMENT_SUMMARY
};

export interface DocumentControlPanelProps {
	currentBlockId: string;
	isChild: boolean;
}

export class AddQuoteProductCatalogDrawerFormValues
	implements Omit<QuoteProduct, "id">
{
	bundled_products: BundledQuoteProduct[] = [];
	bundled_skus: Partial<BundledQuoteProduct>[] = [];
	description = "";
	discount = 0;
	discount_percentage = 0;
	discount_type: DiscountType = DiscountType.Flat;
	item_images: string[] = new Array<string>();
	logo_square: string | null = null;
	name = "";
	id?: string;
	pricing: ProductPricingPlan[] = new Array<ProductPricingPlan>();
	quantity = 1;
	selected_billing_period: ProductBillingType = ProductBillingType.OneTime;
	selected_images: string[] = new Array<string>();
	selected_pricing_id?: string | null;
	selected_supplier_id?: string | null;
	selected_cost_price = 0;
	selected_sell_price = 0;
	selected_setup_discount = 0;
	setup_discount_type: DiscountType = DiscountType.Flat;
	setup_discount_percentage = 0;
	selected_setup_price = 0;
	sell_price = 0;
	sub_total = 0;
	vendor_id = "";
	vendor_product_id = "";
	product_type: ProductType | null = null;
	product_category = "";
	product_source?: ProductSource;
	should_add_setup_price_to_margin = false;
	hide_additional_price = false;
	is_setup_fee_linked_to_quantity = false;
	manufacturer: string | null;
	manufacturer_part_no: string | null;
	selected_distributor?: string | undefined;
	selected_warehouse?: string | null;
	show_individual_product_pricing: boolean;
	show_child_quantity: boolean | null;
	tax_category_id?: string;
	unit_of_measure?: string;
	hide_child_items_in_quote_block: boolean;
	custom_fields?: CustomField[];
	mandatory_custom_field_check_require = false;
	product_thumbnail_size: ProductThumbnailSize | null = null;
	vendor_name?: string;
	vendor_sku_number?: string;
	auto_price_update_details: AutoPriceUpdateDetails | null;
	selected_custom_distributor_details?: SelectedCustomDistributorDetails;
	asin?: string;

	constructor(args: QuoteProduct) {
		this.bundled_products = args.bundled_products || [];
		this.bundled_skus = args.bundled_skus || [];
		this.description = args.description;
		this.discount = args.discount;
		this.discount_type = args.discount_type;
		this.discount_percentage = args.discount_percentage;
		this.item_images = args.item_images;
		this.logo_square = args.logo_square;
		this.name = args.name;
		this.pricing = args.pricing || [];
		this.quantity = args.quantity;
		this.selected_billing_period = args.selected_billing_period;
		this.selected_images = args.selected_images;
		this.selected_pricing_id = args.selected_pricing_id;
		this.selected_cost_price = args.selected_cost_price;
		this.selected_setup_discount = args.selected_setup_discount;
		this.selected_setup_price = args.selected_setup_price;
		this.setup_discount_type = args.setup_discount_type;
		this.setup_discount_percentage = args.setup_discount_percentage || 0;
		this.product_type = args.product_type;
		this.product_category = args.product_category;
		this.product_source = args.product_source;
		this.sell_price = args.sell_price;
		this.sub_total = args.sub_total;
		this.vendor_id = args.vendor_id;
		this.vendor_product_id = args.vendor_product_id;
		this.should_add_setup_price_to_margin =
			args.should_add_setup_price_to_margin;
		this.hide_additional_price = args.hide_additional_price;
		this.manufacturer = args.manufacturer;
		this.manufacturer_part_no = args.manufacturer_part_no;
		this.is_setup_fee_linked_to_quantity =
			args.is_setup_fee_linked_to_quantity;
		this.selected_distributor = args.selected_distributor;
		this.selected_warehouse = args.selected_warehouse;
		this.show_individual_product_pricing =
			args.show_individual_product_pricing;
		this.show_child_quantity = args.show_child_quantity;
		this.tax_category_id = args.tax_category_id;
		this.hide_child_items_in_quote_block =
			args.hide_child_items_in_quote_block;
		this.custom_fields = args.custom_fields;
		this.unit_of_measure = args.unit_of_measure;
		this.mandatory_custom_field_check_require =
			args.mandatory_custom_field_check_require;
		this.product_thumbnail_size = args.product_thumbnail_size;
		this.vendor_name = args.vendor_name;
		this.vendor_sku_number = args.vendor_sku_number;
		this.auto_price_update_details = args.auto_price_update_details;
		this.selected_supplier_id = args.selected_supplier_id;
		this.id = args.id;
		this.asin = args.asin;
	}

	public static fromProduct = (
		product: Product
	): AddQuoteProductCatalogDrawerFormValues => {
		const bundledProducts: BundledQuoteProduct[] =
			product.type === ProductType.Bundle
				? product.bundled_items
						?.filter(a => a.item.type !== ProductType.Bundle)
						.map(
							(
								childProduct: BundleProduct
							): BundledQuoteProduct => {
								const { item } = childProduct;
								const defaultPricing =
									childProduct.item?.pricing?.[0];
								return {
									is_setup_fee_linked_to_quantity:
										childProduct.is_setup_fee_linked_to_quantity ??
										false,
									description: item.description,
									item_images:
										item.item_images ?? new Array<string>(),
									selected_images:
										item.item_images ?? new Array<string>(),
									manufacturer_part_no:
										item.manufacturer_part_no ?? "",
									manufacturer: item.manufacturer ?? "",
									product_type: item.type,
									product_category:
										item.product_category ?? "",
									name: item.name,
									pricing: item.pricing,
									quantity: childProduct.quantity,
									selected_billing_period:
										defaultPricing?.billing_period ||
										ProductBillingType.OneTime,
									selected_cost_price:
										childProduct.selected_cost_price ||
										defaultPricing?.cost_price ||
										1,
									selected_sell_price:
										childProduct.sell_price ||
										defaultPricing?.sell_price ||
										1,
									selected_distributor: undefined,
									selected_pricing_id:
										defaultPricing?.id || null,
									selected_setup_price:
										defaultPricing?.setup_price || 0,
									selected_warehouse: null,
									sell_price:
										childProduct.sell_price ||
										defaultPricing?.sell_price ||
										1,
									sub_total: 1,
									vendor_id: "",
									vendor_product_id: item.id || "",
									vendor_sku_number:
										childProduct?.vendor_sku_no ||
										defaultPricing?.vendor_sku_no,
									is_optional:
										childProduct.is_optional ?? false,
									show_individual_product_pricing: false,
									is_selected:
										childProduct.is_selected ?? false,
									tax_category_id: item.tax_category_id,
									hide_child_items_in_quote_block: false,
									custom_fields: item.custom_fields,
									product_thumbnail_size:
										childProduct.product_thumbnail_size ??
										null,
									mandatory_custom_field_check_require: false,
									discount: childProduct.discount ?? 0,
									discount_percentage: 0,
									discount_type: DiscountType.Flat,
									selected_setup_discount: 0,
									selected_supplier_id:
										childProduct.supplier?.id ||
										defaultPricing.supplier_id,
									setup_discount_type: DiscountType.Flat,
									setup_discount_percentage: 0,
									unit_of_measure: item.unit_of_measure,
									auto_price_update_details:
										item.auto_price_update_details ??
										new AutoPriceUpdateDetails(),
									asin: item.asin
								};
							}
						) ?? []
				: [];
		const quoteProductCatalogDrawerFormValues =
			new AddQuoteProductCatalogDrawerFormValues({
				description: product.description,
				discount: 0,
				discount_type: DiscountType.Flat,
				discount_percentage: 0,
				setup_discount_type: DiscountType.Flat,
				setup_discount_percentage: 0,
				bundled_products: bundledProducts,
				item_images: product.item_images,
				logo_square: product.logo,
				manufacturer_part_no: product.manufacturer_part_no ?? "",
				manufacturer: product.manufacturer ?? "",
				name: product.name,
				pricing: product.pricing,
				id: product.id,
				product_type: product.type,
				product_category: product.product_category ?? "",
				quantity: 1,
				selected_billing_period: ProductBillingType.OneTime,
				selected_cost_price: 0,
				selected_images: product.item_images,
				selected_pricing_id: null,
				selected_setup_discount: 0,
				selected_setup_price: 0,
				sell_price: 0,
				should_add_setup_price_to_margin:
					product.should_add_setup_price_to_margin ?? false,
				sub_total: 0,
				tax_category_id: product.tax_category_id,
				vendor_id: product.vendor_id,
				selected_distributor: undefined,
				show_individual_product_pricing: false,
				show_child_quantity: true,
				selected_warehouse: null,
				hide_additional_price: false,
				is_setup_fee_linked_to_quantity: false,
				hide_child_items_in_quote_block: false,
				vendor_product_id: product.id,
				product_source: product.product_source,
				custom_fields: product.custom_fields,
				mandatory_custom_field_check_require: false,
				product_thumbnail_size: null,
				unit_of_measure: product.unit_of_measure,
				auto_price_update_details: null,
				asin: product.asin
			});
		return quoteProductCatalogDrawerFormValues;
	};

	public static fromSKU = (
		productSKU: SKUProduct
	): AddQuoteProductCatalogDrawerFormValues => {
		const quoteProductCatalogDrawerFormValues =
			new AddQuoteProductCatalogDrawerFormValues({
				description: productSKU.description,
				discount: 0,
				discount_type: DiscountType.Flat,
				discount_percentage: 0,
				setup_discount_type: DiscountType.Flat,
				setup_discount_percentage: 0,
				bundled_products: [],
				bundled_skus: productSKU.bundled_skus,
				item_images: productSKU.images,
				logo_square: productSKU.images?.[0],
				manufacturer_part_no: "",
				manufacturer: "",
				name: productSKU.name,
				pricing: productSKU.pricings.map(
					(pricing: SKUPricingPlanFormValues) =>
						ProductPricingPlan.fromSKUPricingPlan(pricing)
				),
				product_type: productSKU.type,
				product_category: "",
				quantity: 1,
				selected_billing_period: ProductBillingType.OneTime,
				selected_cost_price: 0,
				selected_images: productSKU.images,
				selected_pricing_id: null,
				selected_setup_price: 0,
				selected_setup_discount: 0,
				sell_price: 0,
				should_add_setup_price_to_margin: false,
				sub_total: 0,
				vendor_id: productSKU.vendor_company_id,
				selected_distributor: undefined,
				show_individual_product_pricing: false,
				show_child_quantity: true,
				selected_warehouse: null,
				hide_additional_price: false,
				is_setup_fee_linked_to_quantity: false,
				hide_child_items_in_quote_block: false,
				vendor_product_id: `marketplace:${productSKU.id}`,
				product_source: ProductSource.ZomentumMarketplace,
				custom_fields: productSKU.custom_fields,
				mandatory_custom_field_check_require: false,
				product_thumbnail_size: null,
				unit_of_measure: productSKU.unit_of_measure,
				auto_price_update_details: null
			});
		return quoteProductCatalogDrawerFormValues;
	};
}

export class EditQuoteProductCatalogDrawerFormValues implements QuoteProduct {
	id = "";
	bundled_products: BundledQuoteProduct[] = [];
	description = "";
	discount = 0;
	discount_type = DiscountType.Flat;
	discount_percentage = 0;
	item_images: string[] = new Array<string>();
	logo_square: string | null = null;
	name = "";
	pricing: ProductPricingPlan[] = new Array<ProductPricingPlan>();
	quantity = 1;
	selected_billing_period: ProductBillingType = ProductBillingType.OneTime;
	selected_images: string[] = new Array<string>();
	selected_pricing_id?: string | null;
	selected_supplier_id?: string | null;
	selected_cost_price = 0;
	selected_setup_discount = 0;
	setup_discount_type = DiscountType.Flat;
	setup_discount_percentage = 0;
	selected_setup_price = 0;
	product_type: ProductType | null = null;
	product_category = "";
	product_source?: ProductSource;
	sell_price = 0;
	sub_total = 0;
	vendor_id = "";
	vendor_product_id = "";
	should_add_setup_price_to_margin = false;
	show_child_quantity = true;
	hide_additional_price = false;
	is_setup_fee_linked_to_quantity = false;
	manufacturer: string | null = "";
	manufacturer_part_no: string | null = "";
	selected_distributor?: string | undefined = "";
	show_individual_product_pricing = false;
	hide_child_items_in_quote_block = false;
	selected_warehouse?: string | null;
	tax_category_id?: string;
	unit_of_measure?: string;
	custom_fields?: CustomField[];
	product_thumbnail_size: ProductThumbnailSize | null = null;
	mandatory_custom_field_check_require = false;
	vendor_name?: string;
	vendor_sku_number?: string;
	auto_price_update_details: AutoPriceUpdateDetails | null = null;
	selected_custom_distributor_details?: SelectedCustomDistributorDetails;
	asin?: string = "";

	public static fromQuoteProduct = (
		args: QuoteProduct
	): EditQuoteProductCatalogDrawerFormValues => {
		const defaultValues = new EditQuoteProductCatalogDrawerFormValues();
		if (!args.id) {
			throw new Error("Id does not exists");
		}
		defaultValues.id = args.id;
		defaultValues.bundled_products = args.bundled_products || [];
		defaultValues.description = args.description;
		defaultValues.discount = args.discount;
		defaultValues.discount_type = args.discount_type;
		defaultValues.discount_percentage = args.discount_percentage;
		defaultValues.item_images = args.item_images;
		defaultValues.logo_square = args.logo_square;
		defaultValues.name = args.name;
		defaultValues.pricing = args.pricing || [];
		defaultValues.quantity = args.quantity;
		defaultValues.selected_billing_period = args.selected_billing_period;
		defaultValues.selected_images = args.selected_images;
		defaultValues.selected_pricing_id = args.selected_pricing_id;
		defaultValues.selected_cost_price = args.selected_cost_price;
		defaultValues.selected_setup_discount = args.selected_setup_discount;
		defaultValues.product_type = args.product_type;
		defaultValues.product_category = args.product_category;
		defaultValues.selected_setup_price = args.selected_setup_price;
		defaultValues.sell_price = args.sell_price;
		defaultValues.sub_total = args.sub_total;
		defaultValues.vendor_id = args.vendor_id;
		defaultValues.vendor_product_id = args.vendor_product_id;
		defaultValues.should_add_setup_price_to_margin =
			args.should_add_setup_price_to_margin;
		defaultValues.hide_additional_price = args.hide_additional_price;
		defaultValues.is_setup_fee_linked_to_quantity =
			args.is_setup_fee_linked_to_quantity;
		defaultValues.manufacturer = args.manufacturer;
		defaultValues.manufacturer_part_no = args.manufacturer_part_no;
		defaultValues.selected_distributor = args.selected_distributor;
		defaultValues.show_individual_product_pricing =
			args.show_individual_product_pricing;
		defaultValues.selected_warehouse = args.selected_warehouse;
		defaultValues.tax_category_id = args.tax_category_id;
		defaultValues.unit_of_measure = args.unit_of_measure;
		defaultValues.product_source = args.product_source;
		defaultValues.hide_child_items_in_quote_block =
			args.hide_child_items_in_quote_block;
		defaultValues.custom_fields = args.custom_fields;
		defaultValues.product_thumbnail_size = args.product_thumbnail_size;
		defaultValues.show_child_quantity = args.show_child_quantity ?? false;
		defaultValues.vendor_name = args.vendor_name;
		defaultValues.vendor_sku_number = args.vendor_sku_number;
		defaultValues.auto_price_update_details =
			args.auto_price_update_details;
		defaultValues.selected_supplier_id = args.selected_supplier_id;
		if (
			defaultValues.selected_distributor === Distributor.CustomDistributor
		) {
			defaultValues.selected_custom_distributor_details =
				args.selected_custom_distributor_details;
		}
		defaultValues.asin = args.asin;
		return defaultValues;
	};

	public static fromBundledQuoteProduct = (
		args: BundledQuoteProduct
	): EditQuoteProductCatalogDrawerFormValues => {
		const defaultValues = new EditQuoteProductCatalogDrawerFormValues();
		defaultValues.id = args.id || args.vendor_product_id;
		if (!defaultValues.id) {
			throw new Error("Id does not exists");
		}
		defaultValues.is_setup_fee_linked_to_quantity =
			args.is_setup_fee_linked_to_quantity;
		defaultValues.description = args.description;
		defaultValues.name = args.name;
		defaultValues.pricing = args.pricing || [];
		defaultValues.quantity = args.quantity;
		defaultValues.selected_billing_period = args.selected_billing_period;
		defaultValues.selected_pricing_id = args.selected_pricing_id;
		defaultValues.selected_cost_price = args.selected_cost_price;
		defaultValues.product_type = args.product_type;
		defaultValues.product_category = args.product_category;
		defaultValues.selected_setup_price = args.selected_setup_price;
		defaultValues.sell_price = args.sell_price;
		defaultValues.sub_total = args.sub_total;
		defaultValues.vendor_id = args.vendor_id;
		defaultValues.vendor_product_id = args.vendor_product_id;
		defaultValues.manufacturer = args.manufacturer;
		defaultValues.manufacturer_part_no = args.manufacturer_part_no;
		defaultValues.selected_distributor = args.selected_distributor;
		defaultValues.show_individual_product_pricing =
			args.show_individual_product_pricing;
		defaultValues.selected_warehouse = args.selected_warehouse;
		defaultValues.tax_category_id = args.tax_category_id;
		defaultValues.hide_child_items_in_quote_block =
			args.hide_child_items_in_quote_block;
		defaultValues.custom_fields = args.custom_fields;
		defaultValues.item_images = args.item_images;
		defaultValues.selected_images = args.selected_images;
		defaultValues.selected_supplier_id = args.selected_supplier_id;
		defaultValues.vendor_sku_number = args.vendor_sku_number;
		defaultValues.asin = args.asin;
		defaultValues.unit_of_measure = args.unit_of_measure;
		return defaultValues;
	};
}

export interface BlockComponentProps {
	readonly isFromEditor?: boolean;
	readonly blockId: string | null;
	readonly readonly: boolean;
	readonly isChild: boolean;
	readonly forPdf: boolean;
	readonly hide: boolean;
	readonly isDocumentApproved?: boolean;
	readonly showInputFieldPopup?: boolean;
	readonly isFocused?: boolean;
	readonly doNotHydrateMergeTags?: boolean;
	readonly unhide?: () => void;
	readonly edit?: () => void;
	readonly showReadonlyProductDetails?: boolean;
	readonly isViewOnly?: boolean;
	readonly isPdfPageChild?: boolean;
	readonly currencySettings?: CurrencySettings;
}

export interface ChildBlockComponent extends BlockComponentProps {
	parentId: string;
	blockId: string;
}

export interface CloneBlockRequest {
	block_id: string;
}

export interface MultipleQuoteProductRequest {
	products: QuoteProduct[];
	sectionIndex: number;
	blockId: string;
	productIndex?: number;
}

export enum SavedBlockTab {
	USER_BLOCKS = "user_blocks",
	GLOBAL_BLOCKS = "global_blocks"
}

export interface AssessmentAndQbrCommonBlocks {
	riskMatrix: boolean;
	riskScore: boolean;
}

export interface AssessmentBlocks extends AssessmentAndQbrCommonBlocks {
	riskListing: boolean;
}

export interface QbrBlocks extends AssessmentAndQbrCommonBlocks {
	healthStatus: boolean;
}

export const DefaultSectionDescription: Record<QuoteSectionTypes, string> = {
	[QuoteSectionTypes.SINGLE]: "Select one of the options from below",
	[QuoteSectionTypes.MULTIPLE]: "Select one or more options from below",
	[QuoteSectionTypes.MANDATORY]: ""
};

export interface UpdatePageDetailsRequest {
	page_detail: DocumentPageDetails;
}
